import { type SettingField, settingFieldLabelKey, settingFieldLabelPath, settingFormIssues, settingOptionVocabulary } from '@vello/setting-forms/form';
import type { GovernedValueRow } from '@vello/web-theme';

/**
 * D854 (ADMIN-GOV) — a governed value read in words, for the cards of Regras governadas (in force, per level, history, review).
 * One builder draws the rows (label, text, nested groups) from the key's form; the same rows give the definition list on screen and
 * the one-line text (a toast, a chain row that stays short). Pure: the words come in through `ValueWords`.
 *
 * Units read as a person says them: basis points as a percentage (1.000 → 10%), a factor as `1,5x`, millionths of a degree as
 * degrees, an amount in minor units as money in the market's currency. A value its form does not describe stays as JSON text.
 */
export type ValueRow = GovernedValueRow;

export interface ValueWords {
  readonly t: (id: string, vars?: Readonly<Record<string, string | number>>) => string;
  readonly lang: string;
  readonly v: (vocabulary: string, code: string) => string;
  readonly has: (vocabulary: string, code: string) => boolean;
  /** The ISO 4217 code amounts in minor units are written in (the value's own, else the market's); absent: the bare number. */
  readonly currency?: string | undefined;
}

/** How a stored number is scaled to what a person reads: `divisor` (stored = shown × divisor) and the digits shown at most. */
export interface UnitScale {
  readonly divisor: number;
  readonly digits: number;
  readonly suffix: string;
  /** Written after a space? (`1,5x` and `10%` are not). */
  readonly spaced: false;
}

const SCALES: Readonly<Record<string, UnitScale>> = {
  basisPoints: { divisor: 100, digits: 2, suffix: '%', spaced: false },
  factorBasisPoints: { divisor: 10_000, digits: 4, suffix: 'x', spaced: false },
  microdegrees: { divisor: 1_000_000, digits: 6, suffix: '°', spaced: false },
};

/** The scale of a unit that is stored in a smaller step than it is read in; `undefined` for a unit that reads as stored. */
export function unitScale(unit: string | undefined): UnitScale | undefined {
  return unit === undefined ? undefined : SCALES[unit];
}

const plain = (n: number, lang: string, digits: number): string => new Intl.NumberFormat(lang, { maximumFractionDigits: digits }).format(n);

/** An amount in a currency's minor unit, written as money (`500` BRL → R$ 5,00). Never throws: an unknown code reads as number + code. */
export function formatMinor(minor: number | bigint, currency: string, lang: string): string {
  try {
    const fmt = new Intl.NumberFormat(lang, { style: 'currency', currency });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    return fmt.format(Number(minor) / 10 ** digits);
  } catch {
    return `${String(minor)} ${currency}`;
  }
}

/** A number of a form in words: scaled units as `10%` / `1,5x` / `-33,75°`, an amount as money when the currency is known. */
export function formatUnitNumber(n: number, unit: string | undefined, words: Pick<ValueWords, 'v' | 'lang' | 'currency'>): string {
  const scale = unitScale(unit);
  if (scale !== undefined) return `${plain(n / scale.divisor, words.lang, scale.digits)}${scale.suffix}`;
  if (unit === 'minorUnits' && words.currency !== undefined) return formatMinor(n, words.currency, words.lang);
  const label = unit !== undefined && unit !== 'count' ? words.v('settings.unit', unit) : '';
  return `${plain(n, words.lang, 20)} ${label}`.trim();
}

const lastSegment = (path: string): string => path.replace(/\[\d+\]/g, '').split('.').pop() ?? path;

/** The JSON text of a value (a string stays itself). */
export const valueJson = (value: unknown): string => (typeof value === 'string' ? value : JSON.stringify(value) ?? '—');

/**
 * The rows of a value, read through its form; `undefined` when there is no form or the value does not fit it (the caller then shows
 * the JSON text, so nothing is hidden). A value that is one scalar becomes one row without a label.
 */
export function describeValue(settingKey: string, form: SettingField | undefined, value: unknown, words: ValueWords): readonly ValueRow[] | undefined {
  if (form === undefined || value === undefined || settingFormIssues(form, value).length > 0) return undefined;
  const { t, lang, v, has } = words;
  const labelOf = (path: string): string => {
    const k = settingFieldLabelKey(settingKey, settingFieldLabelPath(form, path));
    return has('settings.field', k) ? v('settings.field', k) : lastSegment(path);
  };
  const option = (f: { readonly labels?: string }, path: string, code: string): string => {
    const id = settingOptionVocabulary(settingKey, settingFieldLabelPath(form, path), f);
    return has(id, code) ? v(id, code) : code;
  };
  const isScalar = (f: SettingField): boolean => f.kind !== 'object' && f.kind !== 'map' && f.kind !== 'list';
  const scalar = (field: SettingField, x: unknown, path: string): string => {
    if (x === null) return t('govset.value.null');
    switch (field.kind) {
      case 'boolean': return x === true ? t('govset.value.yes') : t('govset.value.no');
      case 'integer':
      case 'decimal': return formatUnitNumber(x as number, field.unit, words);
      case 'choice': return option(field, path, x as string);
      case 'choices': {
        const codes = x as readonly string[];
        return codes.length === 0 ? t('govset.value.none') : codes.map((c) => option(field, path, c)).join(', ');
      }
      case 'money': {
        const m = x as { amountMinor: number; currency: string };
        return formatMinor(m.amountMinor, m.currency, lang);
      }
      case 'text': {
        const s = x as string;
        if (s === '') return '—';
        // D883: a code worded by its vocabulary reads in words (an unknown one as the uncatalogued value), never as the code.
        if (field.labels !== undefined) return v(field.labels, s);
        return field.minorAmount === true && words.currency !== undefined && /^\d+$/.test(s) ? formatMinor(BigInt(s), words.currency, lang) : s;
      }
      case 'time':
      case 'date': return (x as string) === '' ? '—' : (x as string);
      case 'json': return JSON.stringify(x) ?? '—';
      default: return valueJson(x);
    }
  };
  /** The rows of a non-scalar value, or a single text for a scalar one. */
  const build = (field: SettingField, x: unknown, path: string): { readonly rows: readonly ValueRow[] } | { readonly text: string } => {
    if (x === null) return { text: t('govset.value.null') };
    switch (field.kind) {
      case 'object': {
        const o = x as Record<string, unknown>;
        const rows: ValueRow[] = [];
        for (const f of field.fields) {
          const p = path === '' ? f.name : `${path}.${f.name}`;
          const present = o[f.name];
          if (present === undefined) {
            // An optional member left out still reads when its form says what absent means (a switch that is "off" unless set).
            if (f.whenAbsent !== undefined) rows.push({ label: labelOf(p), text: `${scalar(f.field, f.whenAbsent, p)} (${t('govset.value.default')})` });
            continue;
          }
          rows.push(row(labelOf(p), f.field, present, p));
        }
        return { rows };
      }
      case 'map': {
        const o = x as Record<string, unknown>;
        const keyId = settingOptionVocabulary(settingKey, settingFieldLabelPath(form, path), { ...(field.keyLabels !== undefined ? { labels: field.keyLabels } : {}) });
        const entries = Object.entries(o);
        if (entries.length === 0) return { text: t('govset.value.none') };
        return { rows: entries.map(([k, y]) => row(has(keyId, k) ? v(keyId, k) : k, field.value, y, path === '' ? k : `${path}.${k}`)) };
      }
      case 'list': {
        const items = x as readonly unknown[];
        if (items.length === 0) return { text: t('govset.value.none') };
        if (!isScalar(field.item)) {
          return { rows: items.map((item, i) => row(t('govset.form.item', { n: String(i + 1) }), field.item, item, `${path}[${String(i)}]`)) };
        }
        return { text: items.map((item, i) => scalar(field.item, item, `${path}[${String(i)}]`)).join(', ') };
      }
      default: return { text: scalar(field, x, path) };
    }
  };
  const row = (label: string, field: SettingField, x: unknown, path: string): ValueRow => {
    const built = build(field, x, path);
    return 'rows' in built ? { label, rows: built.rows } : { label, text: built.text };
  };
  const root = build(form, value, '');
  return 'rows' in root ? root.rows : [{ label: '', text: root.text }];
}

/** The one-line text of rows (`Rota: Sim · Modo: Mais rápida`); a group's rows follow its name, a list's items stand in parentheses. */
export function flattenRows(rows: readonly ValueRow[]): string {
  return rows
    .map((r) => {
      const body = r.rows !== undefined ? flattenRows(r.rows) : r.text ?? '';
      return r.label === '' ? body : `${r.label}: ${body}`;
    })
    .join(' · ');
}

/** How many fields a set of rows holds (a group counts its fields, not itself). */
export function rowCount(rows: readonly ValueRow[]): number {
  return rows.reduce((n, r) => n + (r.rows !== undefined ? rowCount(r.rows) : 1), 0);
}

const pathKey = (path: readonly string[]): string => path.join('\u0001');

function leaves(rows: readonly ValueRow[], path: readonly string[], into: Map<string, string>): void {
  rows.forEach((r, i) => {
    const here = [...path, r.label === '' ? `#${String(i)}` : r.label];
    if (r.rows !== undefined) leaves(r.rows, here, into);
    else into.set(pathKey(here), r.text ?? '');
  });
}

/**
 * What changed from `before` to `after`: only the fields whose text differs (or is new), each with the old text as `was`, inside the
 * groups that hold them. `[]` when nothing differs. Fields that left are named by `removed`.
 */
export function changedRows(before: readonly ValueRow[], after: readonly ValueRow[], removed: string): readonly ValueRow[] {
  const old = new Map<string, string>();
  leaves(before, [], old);
  const walk = (rows: readonly ValueRow[], path: readonly string[]): ValueRow[] => {
    const out: ValueRow[] = [];
    rows.forEach((r, i) => {
      const here = [...path, r.label === '' ? `#${String(i)}` : r.label];
      if (r.rows !== undefined) {
        const inner = walk(r.rows, here);
        if (inner.length > 0) out.push({ label: r.label, rows: inner });
        return;
      }
      const was = old.get(pathKey(here));
      if (was === r.text) return;
      out.push(was === undefined ? { label: r.label, text: r.text ?? '' } : { label: r.label, text: r.text ?? '', was });
    });
    return out;
  };
  const changed = walk(after, []);
  const now = new Map<string, string>();
  leaves(after, [], now);
  const gone: ValueRow[] = [];
  const labels = (rows: readonly ValueRow[], path: readonly string[]): void => {
    rows.forEach((r, i) => {
      const here = [...path, r.label === '' ? `#${String(i)}` : r.label];
      if (r.rows !== undefined) labels(r.rows, here);
      else if (!now.has(pathKey(here))) gone.push({ label: here.filter((p) => !p.startsWith('#')).join(' › '), text: removed });
    });
  };
  labels(before, []);
  return [...changed, ...gone];
}
